import { defineStore } from 'pinia';
import { AuraService, SelectOptionsService } from '@/services';
import { useFilterStore } from '@/stores/modules/filter';
import { normalizeUtcDateRange } from '@/utils/datetime';
import {
    AuraConversation,
    AuraMessage,
    AuraAskRequest,
    AuraAskResponse,
    AuraState,
    AuraFilters,
    AuraResponseBlocks,
    sanitizeAuraHeaderFilters,
} from '@/types';

const normalizeAuraFilters = (filters: AuraFilters | null): AuraFilters | null => {
    if (!filters?.dateFrom || !filters.dateTo) return filters;

    const [dateFrom, dateTo] = normalizeUtcDateRange(filters.dateFrom, filters.dateTo);
    return {
        ...filters,
        dateFrom: dateFrom.toISOString(),
        dateTo: dateTo.toISOString(),
    };
};

const GENERIC_CONVERSATION_TITLE_PATTERN = /^New Analysis(?:\s|$)/;

const getQuestionTitle = (question: string): string =>
    question.length > 80 ? `${question.substring(0, 77)}...` : question;

export const useAuraStore = defineStore('aura', {
    state: (): AuraState => ({
        conversations: [],
        currentConversation: null,
        messages: [],
        activeFilters: null,
        isLoading: false,
        error: null,
        isEnabled: null,
        verticalActivatedAt: null,
        isFiltersPanelOpen: false,
        validationError: null,
        temporalConflict: null,
        validationStep: 'idle',
        availableAgents: [],
        availableQueues: [],
        hasAuxiliaryFilterLoadFailure: false,
    }),

    getters: {
        hasConversations: (state): boolean => state.conversations.length > 0,
        isActiveConversation: (state): boolean => state.currentConversation?.status === 'active',
        hasActiveFilters: (state): boolean => state.activeFilters !== null,
        isAuraEnabled: (state): boolean | null => state.isEnabled,
        auraVerticalActivatedAt: (state): string | null => state.verticalActivatedAt,
        hasValidationError: (state): boolean => state.validationError !== null,
        hasTemporalConflict: (state): boolean => state.temporalConflict !== null,
    },

    actions: {
        async loadHistory(page = 1, limit = 20): Promise<void> {
            this.isLoading = true;
            this.error = null;
            try {
                const response = await AuraService.getHistory(page, limit);
                this.conversations = response.data.items;
            } catch (err) {
                this.error = err instanceof Error ? err.message : 'Failed to load history';
            } finally {
                this.isLoading = false;
            }
        },

        async createConversation(
            title?: string,
            initialQuestion?: string,
        ): Promise<AuraConversation> {
            this.isLoading = true;
            this.error = null;
            try {
                const response = await AuraService.createConversation(title, initialQuestion);
                this.currentConversation = response.conversation;
                this.conversations.unshift(response.conversation);
                this.messages = [];
                // Reset filters but keep default date range (last week, 7 days)
                const today = new Date();
                const sevenDaysAgo = new Date();
                sevenDaysAgo.setDate(today.getDate() - 7);
                const [dateFrom, dateTo] = normalizeUtcDateRange(sevenDaysAgo, today);
                this.activeFilters = {
                    dateFrom: dateFrom.toISOString(),
                    dateTo: dateTo.toISOString(),
                };
                return response.conversation;
            } catch (err) {
                this.error = err instanceof Error ? err.message : 'Failed to create conversation';
                throw err;
            } finally {
                this.isLoading = false;
            }
        },

        async loadConversation(id: number): Promise<void> {
            this.isLoading = true;
            this.error = null;
            try {
                const response = await AuraService.getConversation(id);
                this.currentConversation = response.conversation;
                this.messages = response.messages.map((msg: AuraMessage) => ({
                    ...msg,
                    type: msg.role === 'user' ? 'user' : 'aura',
                    response:
                        (msg as AuraMessage & { structuredResponse?: AuraResponseBlocks })
                            .structuredResponse || undefined,
                }));

                // The header is owned by manual UI selections. Engine metadata
                // may contain temporary semantic scopes and must never be reused.
                const latestUserWithFilters = [...response.messages]
                    .reverse()
                    .find((m: AuraMessage) => m.role === 'user' && m.filters);
                this.activeFilters = sanitizeAuraHeaderFilters(latestUserWithFilters?.filters);
            } catch (err) {
                this.error = err instanceof Error ? err.message : 'Failed to load conversation';
                throw err;
            } finally {
                this.isLoading = false;
            }
        },

        selectConversation(conversation: AuraConversation): void {
            this.currentConversation = conversation;
            this.messages = [];
        },

        async sendMessage(
            question: string,
            overrideFilters?: AuraFilters,
        ): Promise<AuraAskResponse> {
            if (!this.currentConversation) {
                await this.createConversation(undefined, question);
            }

            this.isLoading = true;
            this.error = null;

            // Use override filters if provided, otherwise use active filters from store
            const filters =
                sanitizeAuraHeaderFilters(overrideFilters ?? this.activeFilters) ?? undefined;

            const conversationId = this.currentConversation?.id;
            if (!conversationId) {
                this.isLoading = false;
                throw new Error('No conversation selected');
            }

            // The API persists this same update before calling the engine. Apply
            // it now so a new conversation never remains labelled "New Analysis"
            // while the request is in flight or if the request returns an error.
            if (this.currentConversation?.title?.match(GENERIC_CONVERSATION_TITLE_PATTERN)) {
                const title = getQuestionTitle(question);
                this.currentConversation.title = title;

                const conversationIndex = this.conversations.findIndex(
                    (conversation: AuraConversation) => conversation.id === conversationId,
                );
                if (conversationIndex !== -1) {
                    this.conversations[conversationIndex].title = title;
                }
            }

            // Optimistic user message — rendered immediately so the chat feels
            // responsive while the backend classifies and answers.
            const userMessage: AuraMessage = {
                id: Date.now(),
                conversationId,
                type: 'user',
                content: question,
                question: question,
                filters,
                createdAt: new Date().toISOString(),
            };
            this.messages.push(userMessage);

            try {
                if (this.availableAgents.length === 0 || this.availableQueues.length === 0) {
                    try {
                        const [usersRes, queuesRes] = await Promise.all([
                            SelectOptionsService.getUsers(),
                            SelectOptionsService.getQueues(),
                        ]);
                        this.availableAgents = usersRes.data.map(
                            (u: { id: number; name: string }) => ({
                                id: u.id,
                                name: u.name,
                            }),
                        );
                        this.availableQueues = queuesRes.data.map(
                            (q: { id: number; name: string }) => ({
                                id: q.id,
                                name: q.name,
                            }),
                        );
                    } catch {
                        this.availableAgents = [];
                        this.availableQueues = [];
                        this.hasAuxiliaryFilterLoadFailure = true;
                    }
                }

                const request: AuraAskRequest = {
                    question,
                    conversationId,
                    filters,
                    availableAgents: this.availableAgents,
                    availableQueues: this.availableQueues,
                };

                const response = await AuraService.ask(request);
                // Extract a plain-text content for the message. For blocked
                // responses the summary block is `unavailable`, so we fall back
                // to the top-level status message so the chat bubble still has
                // something to render.
                const messageContent =
                    response.response.summary?.content || response.response.status?.message || '';

                const auraMessage: AuraMessage = {
                    id: response.messageId,
                    conversationId,
                    type: 'aura',
                    content: messageContent,
                    response: response.response,
                    metadata: response.metadata,
                    createdAt: new Date().toISOString(),
                };

                this.messages.push(auraMessage);

                return response;
            } catch (err) {
                // Roll back the optimistic user message so the UI does not
                // leave an orphaned bubble when the request fails.
                const index = this.messages.findIndex((m) => m.id === userMessage.id);
                if (index !== -1) {
                    this.messages.splice(index, 1);
                }
                // Extract error message from backend response if available
                const axiosError = err as {
                    response?: { data?: { error?: { message?: string } } };
                };
                const errorMessage =
                    axiosError.response?.data?.error?.message ||
                    (err instanceof Error ? err.message : 'Failed to send message');
                this.error = errorMessage;
                throw err;
            } finally {
                this.isLoading = false;
            }
        },

        async deleteConversation(id: number): Promise<void> {
            this.isLoading = true;
            this.error = null;
            try {
                await AuraService.deleteConversation(id);
                // Remove from conversations list
                this.conversations = this.conversations.filter(
                    (c: AuraConversation) => c.id !== id,
                );
                // Clear current if deleted
                if (this.currentConversation?.id === id) {
                    this.currentConversation = null;
                    this.messages = [];
                }
            } catch (err) {
                this.error = err instanceof Error ? err.message : 'Failed to delete conversation';
                throw err;
            } finally {
                this.isLoading = false;
            }
        },

        async deleteConversationMessages(
            conversationId: number,
            messageIds: number[],
        ): Promise<void> {
            try {
                const persistedIds = messageIds.filter(
                    (id) => typeof id === 'number' && id < 1000000000000,
                );
                if (persistedIds.length > 0) {
                    await AuraService.deleteConversationMessages(conversationId, persistedIds);
                }
                this.messages = this.messages.filter((msg) => !messageIds.includes(msg.id));
            } catch (err) {
                this.error = err instanceof Error ? err.message : 'Failed to delete messages';
                throw err;
            }
        },

        clearError(): void {
            this.error = null;
        },

        consumeAuxiliaryFilterLoadFailure(): boolean {
            const hasFailure = this.hasAuxiliaryFilterLoadFailure;
            this.hasAuxiliaryFilterLoadFailure = false;
            return hasFailure;
        },

        setValidationError(error: AuraState['validationError']): void {
            this.validationError = error;
        },

        clearValidationError(): void {
            this.validationError = null;
        },

        setTemporalConflict(conflict: AuraState['temporalConflict']): void {
            this.temporalConflict = conflict;
        },

        clearTemporalConflict(): void {
            this.temporalConflict = null;
        },

        setValidationStep(step: AuraState['validationStep']): void {
            this.validationStep = step;
        },

        addErrorMessage(message: string): void {
            const errorMessage: AuraMessage = {
                id: Date.now(),
                conversationId: this.currentConversation?.id || 0,
                type: 'error',
                content: message,
                createdAt: new Date().toISOString(),
            };
            this.messages.push(errorMessage);
        },

        setFilters(filters: AuraFilters | null): void {
            this.activeFilters = normalizeAuraFilters(filters);
        },

        clearFilters(): void {
            this.activeFilters = null;
        },

        openFiltersPanel(): void {
            this.isFiltersPanelOpen = true;
        },

        closeFiltersPanel(): void {
            this.isFiltersPanelOpen = false;
        },

        reset(preserveContext = false): void {
            this.conversations = [];
            this.currentConversation = null;
            this.messages = [];
            this.activeFilters = null;
            this.isLoading = false;
            this.error = null;
            if (!preserveContext) {
                this.isEnabled = null;
                this.verticalActivatedAt = null;
            }
            this.isFiltersPanelOpen = false;
            this.validationError = null;
            this.temporalConflict = null;
            this.validationStep = 'idle';
            this.availableAgents = [];
            this.availableQueues = [];
            this.hasAuxiliaryFilterLoadFailure = false;
        },

        async checkContext(): Promise<boolean> {
            const filterStore = useFilterStore();
            const requestedLevelId = filterStore.level?.id ?? null;
            this.isEnabled = null;
            this.verticalActivatedAt = null;
            if (requestedLevelId === null) return false;

            try {
                const response = await AuraService.getContext();
                if (filterStore.level?.id !== requestedLevelId) return false;
                this.isEnabled = response.data.enabled;
                this.verticalActivatedAt = response.data.linkedVertical?.activatedAt ?? null;
                return this.isEnabled;
            } catch (err) {
                if (filterStore.level?.id !== requestedLevelId) return false;
                this.isEnabled = false;
                this.verticalActivatedAt = null;
                return false;
            }
        },
    },
});
